import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  RichTextEditor,
} from '@mlm/ui';
import {
  createTestimonial,
  deleteTestimonial,
  listTestimonials,
  updateTestimonial,
  type Testimonial,
  type TestimonialInput,
} from '../lib/api';

/**
 * Per-product testimonials editor — embedded as the product editor's
 * "Testimonials" tab. Testimonials are per-product in the data model
 * (`product_testimonials.product_id`); this scopes the list + dialog to one
 * product (no product picker), reusing the `/admin/testimonials?productId=`
 * routes. Shown on the storefront product page's "Testimony" tab.
 */
export function ProductTestimonialsSection({ productId }: { productId: string }) {
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [creating, setCreating] = useState(false);

  const testimonials = useQuery({
    queryKey: ['admin-testimonials', productId],
    queryFn: () => listTestimonials(productId),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Admin-authored testimonials shown on this product's “Testimony” tab in the store.
        </p>
        <Button size="sm" onClick={() => setCreating(true)}>
          New testimonial
        </Button>
      </div>

      {testimonials.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : (testimonials.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No testimonials yet for this product.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="p-3">Title</th>
                  <th className="p-3">Video</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {(testimonials.data ?? []).map((t) => (
                  <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="p-3 font-medium">{t.title}</td>
                    <td className="p-3">{t.youtubeUrl ? 'Yes' : '—'}</td>
                    <td className="p-3">
                      <Badge variant={t.active ? 'default' : 'secondary'}>
                        {t.active ? 'Active' : 'Hidden'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(creating || editing) && (
        <TestimonialDialog
          productId={productId}
          testimonial={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function TestimonialDialog({
  productId,
  testimonial,
  onClose,
}: {
  productId: string;
  testimonial: Testimonial | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(testimonial?.title ?? '');
  const [youtubeUrl, setYoutubeUrl] = useState(testimonial?.youtubeUrl ?? '');
  const [authorName, setAuthorName] = useState(testimonial?.authorName ?? '');
  const [bodyHtml, setBodyHtml] = useState(testimonial?.bodyHtml ?? '');
  const [sortOrder, setSortOrder] = useState(String(testimonial?.sortOrder ?? 0));
  const [active, setActive] = useState(testimonial?.active ?? true);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-testimonials', productId] });
  const save = useMutation({
    mutationFn: () => {
      const input: TestimonialInput = {
        productId,
        title: title.trim(),
        youtubeUrl: youtubeUrl.trim() || null,
        bodyHtml: bodyHtml.trim() || null,
        authorName: authorName.trim() || null,
        sortOrder: Number(sortOrder) || 0,
        active,
      };
      return testimonial ? updateTestimonial(testimonial.id, input) : createTestimonial(input);
    },
    meta: { successMessage: 'Testimonial saved.' },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteTestimonial(testimonial!.id),
    meta: { successMessage: 'Testimonial deleted.' },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const canSave = !!title.trim() && !save.isPending;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{testimonial ? 'Edit testimonial' : 'New testimonial'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Customer success story" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">YouTube URL (optional)</label>
              <Input value={youtubeUrl} onChange={(e) => setYoutubeUrl(e.target.value)} placeholder="https://youtu.be/…" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Author name (optional)</label>
              <Input value={authorName} onChange={(e) => setAuthorName(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Body</label>
            <RichTextEditor value={bodyHtml} onChange={setBodyHtml} placeholder="Write the testimonial — format text, set color and size…" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Sort order</label>
              <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 pt-5">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>Active (visible in store)</span>
            </label>
          </div>
          {save.isError && <p className="text-destructive">Could not save. Check the fields.</p>}
          <div className="flex justify-between gap-2 pt-1">
            <div>
              {testimonial && (
                <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate()}>
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={save.isPending}>
                Cancel
              </Button>
              <Button onClick={() => save.mutate()} disabled={!canSave}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
